import { useStore } from '@vromo/mobx-react';
import { Job } from '@vromo/models';
import { RootStore } from '../stores/RootStore';
import { last } from 'lodash';
import { useState } from 'react';

export type FetchJobParams = {
    _all_finished__between?: string;
    _all_finished__order?: string;
    _all_attr__include?: boolean;
    _all_route__include?: boolean;
    _all_dispatches__include?: boolean;
    _all_stages__all_analytics_clicksTotal__include?: boolean;
    _all_group__in?: string;
    _all_site__exists_id__in?: string;
};

const MAX_FETCH_SIZE = 200;
export const MAX_JOBS = 20000;

interface BatchFetchJobsProps {
    params: FetchJobParams;
    startDate: Date;
    endDate: Date;
    filterFn?: (it: Job[]) => Job[];
}

const useBatchFetchJobs = (): {
    batchFetchJobs: (it: BatchFetchJobsProps) => Promise<Job[]>;
    loadingJobs: boolean;
} => {
    const {
        authStore: { graph, role }
    } = useStore<RootStore>('rootStore');

    const [loadingJobs, setLoadingJobs] = useState(false);

    const fetchJobRange = (start: Date, end: Date, size: number, params: FetchJobParams) => {
        return graph.root
            .graph()
            .roles(role.id)
            .jobs()
            .get({
                _all_finished__order: 'desc',
                _size__lte: size,
                _all_finished__between: `[${start.valueOf()}..${end.valueOf()})`,
                ...params
            });
    };

    const batchFetchJobs = ({
        params,
        startDate,
        endDate,
        filterFn = (it: Job[]) => it
    }: BatchFetchJobsProps): Promise<Job[]> => {
        return new Promise(async (resolve, reject) => {
            let jobs: Job[] = [];
            let dateLimit = endDate;
            let lastFetchSize = MAX_FETCH_SIZE;
            let abort = false;
            setLoadingJobs(true);

            while (
                startDate <= dateLimit &&
                lastFetchSize === MAX_FETCH_SIZE &&
                jobs.length < MAX_JOBS &&
                !abort
            ) {
                await fetchJobRange(startDate, dateLimit, MAX_FETCH_SIZE, params)
                    .then(({ items }) => {
                        if (items.length) {
                            lastFetchSize = items.length;
                            const lastJobFinished = last(items)?.finished;
                            if (lastJobFinished) dateLimit = new Date(lastJobFinished);
                            jobs.push(...filterFn(items));
                            if (jobs.length >= MAX_JOBS) {
                                jobs = jobs.slice(0, MAX_JOBS);
                            }
                        } else {
                            lastFetchSize = 0;
                        }
                    })
                    .catch((e) => {
                        abort = true;
                        jobs = [];
                        dateLimit = endDate;
                        setLoadingJobs(false);
                        reject(e);
                    });
            }

            setLoadingJobs(false);
            resolve(jobs);
        });
    };

    return { batchFetchJobs, loadingJobs };
};

export default useBatchFetchJobs;
